/**
 * The quote-request page.
 *
 * `<special-offer-form>` owns the state; the child elements are dumb and talk to it
 * through events, so the summary, the line list and the form can never disagree about
 * what is being quoted.
 *
 * Money is handled in integer cents everywhere and only formatted at the edge — a
 * float euro would drift on the VAT line.
 *
 * A classic script, not an ES module: relative `import` does not resolve against
 * Shopify's hashed CDN asset urls, and a failed import would leave the custom elements
 * undefined — the form would then submit natively and reload the page. `OfferList` and
 * the money formatter are taken from the globals that `offer-list.js` and
 * `offer-money.js` publish; both are loaded before this script.
 */

const OfferList = window.OfferList;
const formatMoney = window.offerFormMoney;

const strings = () => window.offerFormStrings ?? {};

/* ------------------------------------------------------------------ */
/* quantity picker                                                     */
/* ------------------------------------------------------------------ */

/**
 * Stepper for one offer line. Enforces the variant's minimum order quantity, which
 * comes from the same `custom.quantity_breaks` metafield the PDP uses.
 *
 * The input is only clamped on `change`/blur, not on every keystroke, so typing "1" on
 * the way to "150" is not rewritten to the minimum under the customer's cursor.
 */
if (!customElements.get('offer-quantity')) {
  customElements.define(
    'offer-quantity',
    class OfferQuantity extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-offer-quantity-input]');
        if (!this.input) return;

        this.min = Number(this.input.min) || 1;

        this.querySelector('[data-offer-quantity-decrease]')?.addEventListener('click', () =>
          this.step(-1)
        );
        this.querySelector('[data-offer-quantity-increase]')?.addEventListener('click', () =>
          this.step(1)
        );

        this.input.addEventListener('change', this.onChange);
      }

      step(direction) {
        const next = (Number(this.input.value) || this.min) + direction;
        this.commit(next);
      }

      onChange = () => this.commit(Number(this.input.value));

      commit(value) {
        const quantity = Number.isFinite(value) ? Math.max(Math.round(value), this.min) : this.min;

        this.input.value = quantity;
        this.dispatchEvent(
          new CustomEvent('offer-quantity:change', {
            bubbles: true,
            detail: { variantId: Number(this.dataset.variantId), quantity },
          })
        );
      }
    }
  );
}

/* ------------------------------------------------------------------ */
/* file upload                                                         */
/* ------------------------------------------------------------------ */

/**
 * Logo/print-file picker.
 *
 * Shopify's contact form cannot carry a binary, so nothing is submitted with the form
 * itself: each file is uploaded through `cartFileUpload` as soon as it is picked, and only
 * the resulting CDN url travels with the form, as a plain hidden text field.
 *
 * Without an upload product configured the element still validates and lists the picked
 * files and submits their *names*, so the merchant can chase the assets by email.
 */
if (!customElements.get('offer-file-upload')) {
  customElements.define(
    'offer-file-upload',
    class OfferFileUpload extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('[data-offer-file-input]');
        this.list = this.querySelector('[data-offer-file-list]');
        this.errorTarget = this.querySelector('[data-offer-file-error]');
        this.dropzone = this.querySelector('[data-offer-file-dropzone]');
        if (!this.input) return;

        this.variantId = this.dataset.uploadVariant || '';
        this.maxFiles = Number(this.dataset.maxFiles) || 4;
        this.maxBytes = (Number(this.dataset.maxSize) || 10) * 1024 * 1024;
        this.accepted = (this.dataset.accept || '')
          .split(',')
          .map((each) => each.trim().toLowerCase())
          .filter(Boolean);

        /** @type {Array<{name: string, url: string, status: string}>} */
        this.files = [];

        this.input.addEventListener('change', () => {
          this.addFiles([...this.input.files]);
          // Clearing lets the same file be re-picked after a removal.
          this.input.value = '';
        });

        this.dropzone?.addEventListener('dragover', this.onDragOver);
        this.dropzone?.addEventListener('dragleave', this.onDragLeave);
        this.dropzone?.addEventListener('drop', this.onDrop);
      }

      onDragOver = (event) => {
        event.preventDefault();
        this.dropzone.classList.add('special-offer-form__dropzone--dragging');
      };

      onDragLeave = () => {
        this.dropzone.classList.remove('special-offer-form__dropzone--dragging');
      };

      onDrop = (event) => {
        event.preventDefault();
        this.dropzone.classList.remove('special-offer-form__dropzone--dragging');
        this.addFiles([...event.dataTransfer.files]);
      };

      /** True while at least one file is still in flight — the form waits on this. */
      get isUploading() {
        return this.files.some((file) => file.status === 'uploading');
      }

      /** Without an upload product files are listed but only their names submitted. */
      get canUpload() {
        return Boolean(this.variantId && window.cartFileUpload);
      }

      addFiles(incoming) {
        this.setError('');

        for (const file of incoming) {
          if (this.files.length >= this.maxFiles) {
            this.setError(this.format(strings().fileTooMany, { count: this.maxFiles }));
            break;
          }
          if (!this.isAccepted(file)) {
            this.setError(this.format(strings().fileWrongType, { name: file.name }));
            continue;
          }
          if (file.size > this.maxBytes) {
            this.setError(this.format(strings().fileTooLarge, { name: file.name }));
            continue;
          }

          const entry = { name: file.name, url: '', status: this.canUpload ? 'uploading' : 'local' };
          this.files.push(entry);

          if (this.canUpload) this.upload(file, entry);
        }

        this.render();
      }

      isAccepted(file) {
        if (!this.accepted.length) return true;
        const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
        return this.accepted.includes(extension);
      }

      async upload(file, entry) {
        try {
          entry.url = await window.cartFileUpload.upload(file, this.variantId);
          entry.status = 'done';
        } catch (error) {
          console.error('Uploading a print file failed', error);
          entry.status = 'error';
          this.setError(this.format(strings().fileUploadFailed, { name: file.name }));
        }

        this.render();
      }

      removeFile(index) {
        this.files.splice(index, 1);
        this.setError('');
        this.render();
      }

      /**
       * The hidden inputs are rebuilt from scratch on every render, so a removed file
       * can never leave a stale value behind to be submitted.
       */
      render() {
        if (!this.list) return;

        this.list.innerHTML = '';

        this.files.forEach((file, index) => {
          const item = document.createElement('li');
          item.className = `special-offer-form__file special-offer-form__file--${file.status}`;

          const name = document.createElement('span');
          name.className = 'special-offer-form__file-name';
          name.textContent = file.name;

          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'special-offer-form__file-remove';
          remove.setAttribute('aria-label', this.format(strings().fileRemove, { name: file.name }));
          remove.textContent = '×';
          remove.addEventListener('click', () => this.removeFile(index));

          item.append(name, remove);

          const value = file.url || file.name;
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = `contact[${this.format(strings().fileFieldLabel, { index: index + 1 })}]`;
          hidden.value = file.status === 'uploading' ? '' : value;
          item.append(hidden);

          this.list.append(item);
        });

        this.dispatchEvent(new CustomEvent('offer-files:change', { bubbles: true }));
      }

      setError(message) {
        if (!this.errorTarget) return;
        this.errorTarget.textContent = message ?? '';
        this.errorTarget.hidden = !message;
      }

      format(template, values) {
        if (!template) return '';
        return Object.entries(values).reduce(
          (result, [key, value]) => result.replace(`[${key}]`, value),
          template
        );
      }
    }
  );
}

/* ------------------------------------------------------------------ */
/* section controller                                                  */
/* ------------------------------------------------------------------ */

if (!customElements.get('special-offer-form')) {
  customElements.define(
    'special-offer-form',
    class SpecialOfferForm extends HTMLElement {
      connectedCallback() {
        this.moneyFormat = this.dataset.moneyFormat || '{{ amount_with_comma_separator }} €';
        this.finishingCost = Number(this.dataset.finishingCost) || 0;
        this.shippingCost = Number(this.dataset.shippingCost) || 0;
        this.taxRate = Number(this.dataset.taxRate) || 0;
        this.country = (this.dataset.country || 'DE').toUpperCase();
        this.pzBase = this.dataset.pzBase || '';
        this.estimate = null;
        this.estimatePending = '';
        this.finishingLabel = this.dataset.finishingLabel || '';

        this.linesTarget = this.querySelector('[data-offer-lines]');
        this.emptyState = this.querySelector('[data-offer-empty]');
        this.body = this.querySelector('[data-offer-body]');
        this.successState = this.querySelector('[data-offer-success]');
        // The contact form is the only <form> in the section. Matched by tag rather than by
        // id: `{% form %}` owns the id and a mismatch would silently drop the submit
        // handler, letting the browser fall through to a native submit and reload the page.
        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[data-offer-submit]');
        this.template = this.querySelector('[data-offer-line-template]');
        this.summaryInput = this.querySelector('[data-offer-summary-input]');
        this.upload = this.querySelector('offer-file-upload');

        this.addEventListener('offer-quantity:change', this.onQuantityChange);
        this.addEventListener('click', this.onRemoveClick);
        this.addEventListener('offer-files:change', this.onFilesChange);
        this.addEventListener('change', (event) => { if (event.target && event.target.matches && event.target.matches('[data-offer-express], [data-offer-datencheck], [name="contact[Druckdaten]"]')) this.render(); }); // pz-patch23
        // Bound on the element, not the form: `submit` bubbles, so this fires even if the
        // form reference is ever wrong, and can always cancel the native submit.
        this.addEventListener('submit', this.onSubmit);
        // pz-patch56: rote Markierung verschwindet, sobald das Feld ausgefüllt wird
        this.addEventListener('input', (event) => { if (event.target && event.target.classList) event.target.classList.remove('special-offer-form__input--invalid'); });

        this.unsubscribe = OfferList.subscribe(() => this.render());

        // Shopify bounces a successful contact form back with `?contact_posted=true`, so a
        // hard-navigated submit (JS disabled, or a fetch that fell back) still lands on the
        // thank-you state — and the list is cleared there too, not only in the fetch path.
        if (new URLSearchParams(window.location.search).get('contact_posted') === 'true') {
          this.submitted = true;
          this.showSuccess({ scroll: false });
          OfferList.clear();
          return;
        }

        this.render();
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      /* ---------- lines ---------- */

      onQuantityChange = (event) => {
        const { variantId, quantity } = event.detail;
        OfferList.update(variantId, quantity);
      };

      onRemoveClick = (event) => {
        const button = event.target.closest('[data-offer-remove]');
        if (!button) return;

        OfferList.remove(Number(button.dataset.offerRemove));
      };

      /* ---------- files ---------- */

      /** The submit button spins and stays disabled until every picked file has a url. */
      onFilesChange = () => {
        if (this.submitting) return;

        const uploading = Boolean(this.upload?.isUploading);
        this.toggleLoading(uploading);
        if (!uploading) this.setFormError('');
      };

      render() {
        // Once the request is submitted the success panel owns the view; a later list
        // change (the post-submit clear, or another tab) must not swap it for the empty
        // state.
        if (this.submitted) return;

        const lines = OfferList.read();

        this.setPageState(lines.length ? 'filled' : 'empty');
        this.body?.toggleAttribute('hidden', lines.length === 0);
        this.emptyState?.toggleAttribute('hidden', lines.length > 0);

        this.renderLines(lines);
        this.renderSummary(lines);
      }

      renderLines(lines) {
        if (!this.linesTarget || !this.template) return;

        this.linesTarget.innerHTML = '';

        for (const line of lines) {
          const node = this.template.content.cloneNode(true);
          const unitPrice = OfferList.unitPrice(line, OfferList.groupQuantity(line, lines));
          // A size line of a split order is held to the group's total, not its own minimum.
          const min = OfferList.minimumFor(line, lines);
          const removable = OfferList.canRemove(line, lines);

          // Every link in the card points at the product; there are two of them.
          for (const link of node.querySelectorAll('[data-offer-line-link]')) {
            link.href = line.url || '#';
          }

          const image = node.querySelector('[data-offer-line-image]');
          if (image) {
            if (line.image) {
              image.src = line.image;
              image.alt = line.title;
            } else {
              image.remove();
            }
          }

          this.setText(node, '[data-offer-line-title]', line.title);
          this.setText(node, '[data-offer-line-variant]', line.variantTitle);
          this.setText(node, '[data-offer-line-price]', formatMoney(unitPrice, this.moneyFormat));
          this.setText(
            node,
            '[data-offer-line-sku]',
            line.sku ? this.format(strings().sku, { sku: line.sku }) : ''
          );

          // Only worth striking through the original once a tier has actually cut the price.
          const compare = node.querySelector('[data-offer-line-compare]');
          if (compare) {
            const discounted = unitPrice < line.price;
            compare.textContent = discounted ? formatMoney(line.price, this.moneyFormat) : '';
            compare.hidden = !discounted;
          }

          const quantity = node.querySelector('offer-quantity');
          if (quantity) quantity.dataset.variantId = line.variantId;

          const input = node.querySelector('[data-offer-quantity-input]');
          if (input) {
            input.value = line.quantity;
            input.min = min;
            input.id = `offer-quantity-${line.variantId}`;
            input.setAttribute('aria-label', this.format(strings().quantityLabel, { title: line.title }));
          }

          // At the minimum a decrement can do nothing, so the stepper offers removal
          // instead — the same swap the cart drawer makes. When removal would break the
          // group's minimum too, the decrement stays in place, disabled.
          const atMinimum = line.quantity <= min;
          const decrease = node.querySelector('[data-offer-quantity-decrease]');
          const stepRemove = node.querySelector('[data-offer-quantity-remove]');
          if (decrease) {
            decrease.hidden = atMinimum && removable;
            decrease.disabled = atMinimum && !removable;
          }
          if (stepRemove) {
            stepRemove.hidden = !(atMinimum && removable);
            stepRemove.dataset.offerRemove = line.variantId;
          }

          // Selected by its own hook: by now the stepper's remove button above also carries
          // `data-offer-remove` and comes first in the DOM, so that selector would miss this one.
          const remove = node.querySelector('[data-offer-line-remove]');
          if (remove) {
            remove.disabled = !removable;
            remove.dataset.offerRemove = line.variantId;
            remove.setAttribute('aria-label', this.format(strings().removeLabel, { title: line.title }));
          }

          this.linesTarget.append(node);
        }
      }

      /* ---------- summary ---------- */

      /**
       * Finishing ("Veredlung") and shipping are flat, merchant-configured amounts: this
       * is a quote, not a cart, so there is no rate engine to ask. Tax is computed on the
       * subtotal *including* both, matching the German gross-price convention the design
       * shows (908,00 € × 19% = 172,52 €).
       */
      totals(lines) {
        const products = lines.reduce(
          (total, line) => total + OfferList.unitPrice(line, OfferList.groupQuantity(line, lines)) * line.quantity,
          0
        );
        const quantity = lines.reduce((total, line) => total + line.quantity, 0);
        // Veredelung: Designer-Zeilen (Gruppe pz:) haben sie schon im Stückpreis, sonst ca.-Schätzung aus dem Designer (Standard-Verfahren, Hauptfläche)
        const open = lines.filter((line) => !this.isDesignerLine(line));
        const ready = !open.length || (this.estimate && this.estimate.key === this.estimateKey(open));
        const finishing = !open.length ? 0 : ready ? this.estimate.finishing : null;
        const unknown = open.length && ready ? this.estimate.unknown : 0;
        const expressBox = this.querySelector('[data-offer-express]');
        const express = expressBox && expressBox.checked ? Math.round((products + (finishing || 0)) * 0.1) : 0; // Express: +10 % vom Warenwert (Produkte + Veredelung, wie im Designer)
        const dcBox = this.querySelector('[data-offer-datencheck]');
        const datencheck = dcBox && dcBox.checked ? 990 : 0; // Datencheck separat, nicht in Express enthalten
        const goods = products + (finishing || 0) + express + datencheck;
        const shipping = lines.length ? this.shippingFor(goods) : 0;
        return { products, quantity, finishing, unknown, shipping, express, datencheck, goods, designerOnly: lines.length > 0 && !open.length };
      }

      isDesignerLine(line) {
        return typeof line.group === 'string' && line.group.indexOf('pz:') === 0;
      }

      estimateKey(open) {
        return open.map((line) => line.productId + ':' + line.quantity).sort().join(',');
      }

      /** Versand wie im Checkout: DE 5,90 €, AT 9,90 € (ab 150 € frei), übrige EU 13,99 €, sonst 19,99 €. */
      shippingFor(goods) {
        const c = this.country;
        if (c === 'DE' || c === 'AT') return goods >= 15000 ? 0 : c === 'DE' ? 590 : 990;
        const eu = ['BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'];
        return eu.indexOf(c) >= 0 ? 1399 : 1999;
      }

      requestEstimate(lines) {
        const open = lines.filter((line) => !this.isDesignerLine(line));
        const key = this.estimateKey(open);
        if (!open.length || !this.pzBase || key === this.estimatePending || (this.estimate && this.estimate.key === key)) return;
        this.estimatePending = key;
        clearTimeout(this.estimateTimer);
        this.estimateTimer = setTimeout(() => {
          fetch(this.pzBase + '/api/quote-list/estimate', { method: 'POST', body: JSON.stringify({ items: open.map((line) => ({ productId: line.productId, quantity: line.quantity })) }) })
            .then((response) => response.json())
            .then((data) => { this.estimate = { key, finishing: Number(data.finishing_cents) || 0, unknown: Number(data.unknown) || 0 }; })
            .catch(() => { this.estimate = { key, finishing: 0, unknown: open.length }; })
            .finally(() => { this.estimatePending = ''; this.renderSummary(OfferList.read()); });
        }, 300);
      }

      approx(cents) {
        return 'ca. ' + formatMoney(cents, this.moneyFormat);
      }

      finishingText(totals) {
        if (totals.designerOnly) return 'im Stückpreis enthalten'; // Designer-Positionen: Veredelung steckt schon im Stückpreis
        if (totals.finishing === null) return 'wird berechnet …';
        if (totals.unknown && !totals.finishing) return 'im Angebot';
        return this.approx(totals.finishing) + (totals.unknown ? ' + weitere im Angebot' : '');
      }

      renderSummary(lines) {
        this.requestEstimate(lines);
        const totals = this.totals(lines);

        this.setText(this, '[data-summary-products]', this.approx(totals.products));
        this.setText(this, '[data-summary-count]', String(totals.quantity));
        this.setText(this, '[data-summary-finishing]', this.finishingText(totals));
        // Veredelungs-Chip: Verfahren aus den Designer-Positionen („… (Lasergravur)“), sonst ausgeblendet
        const chip = this.querySelector('[data-summary-finishing-chip]');
        if (chip) {
          const methods = new Set();
          lines.forEach((line) => {
            if (!line.group) return;
            (String(line.variantTitle || '').match(/\(([^)]+)\)/g) || []).forEach((m) => m.slice(1, -1).split(/\s*,\s*/).forEach((x) => x && methods.add(x)));
          });
          chip.textContent = [...methods].join(' · ');
          chip.style.display = methods.size ? '' : 'none';
        }
        this.setText(this, '[data-summary-shipping]', lines.length && !totals.shipping ? 'kostenlos' : this.approx(totals.shipping));
        const expressRow = this.querySelector('[data-summary-express-row]');
        if (expressRow) expressRow.hidden = !totals.express;
        this.setText(this, '[data-summary-express]', this.approx(totals.express));
        const dcRow = this.querySelector('[data-summary-datencheck-row]');
        if (dcRow) dcRow.hidden = !totals.datencheck;
        this.setText(this, '[data-summary-datencheck]', formatMoney(totals.datencheck, this.moneyFormat));

        // pz-patch56: Ca. Gesamt (netto) direkt am Absenden-Button – alles gebündelt inkl. Versand
        const grandRow = this.querySelector('[data-summary-grand-row]');
        if (grandRow) grandRow.hidden = !lines.length;
        const pending = totals.finishing === null;
        this.setText(this, '[data-summary-grand]', pending ? 'wird berechnet …' : this.approx(totals.goods + totals.shipping));
        const grandNote = this.querySelector('[data-summary-grand-note]');
        if (grandNote) grandNote.textContent = totals.unknown ? (grandNote.dataset.unknown || '') : (grandNote.dataset.default || '');

        if (this.summaryInput) this.summaryInput.value = this.serialise(lines, totals);
      }

      /**
       * The line items only reach the merchant as text — a contact form has no structured
       * payload — so they are rendered as a readable block rather than JSON.
       */
      serialise(lines, totals) {
        const rows = lines.map((line) => {
          const unitPrice = OfferList.unitPrice(line, OfferList.groupQuantity(line, lines));
          const unit = formatMoney(unitPrice, this.moneyFormat);
          const total = formatMoney(unitPrice * line.quantity, this.moneyFormat);
          const variant = line.variantTitle ? ` (${line.variantTitle})` : '';
          const sku = line.sku ? ` [${line.sku}]` : '';

          const draft = typeof line.group === 'string' && line.group.indexOf('pz:') === 0 && line.url ? ` — Entwurf: ${line.url}` : '';
          return `${line.quantity}× ${line.title}${variant}${sku} — ${unit}/Stk. = ${total}${draft}`;
        });

        return [
          ...rows,
          '',
          'Unverbindliche ca.-Angaben (netto zzgl. MwSt.):',
          `Produkte: ${this.approx(totals.products)}`,
          `Veredelung: ${this.finishingText(totals)}`,
          `Versand: ${totals.shipping ? this.approx(totals.shipping) : 'kostenlos'}`,
          ...(totals.express ? [`Express-Bearbeitung (+10 % vom Warenwert): ${this.approx(totals.express)}`] : []),
          `Druckdaten: ${(this.querySelector('[name="contact[Druckdaten]"]:checked') || {}).value || '–'}`,
          ...(totals.datencheck ? ['Professioneller Datencheck: 9,90 €'] : []),
          `Ca. Gesamt netto inkl. Versand: ${totals.finishing === null ? '–' : this.approx(totals.goods + totals.shipping)}${totals.unknown ? ' (zzgl. Veredelung im Angebot)' : ''}`,
        ].join('\n');
      }

      /* ---------- submit ---------- */

      /**
       * Submitted over fetch so the success state can be animated in place instead of
       * reloading the page. Shopify answers a contact form with a 302 to
       * `?contact_posted=true`, which `fetch` follows transparently — so a response whose
       * final url carries that flag is the success signal. Anything else is surfaced as
       * an error and the list is left intact, so nothing is lost.
       *
       * The default is cancelled *first*, on every path: bailing out before
       * `preventDefault()` would let the browser fall through to a native submit and
       * reload the page, which is exactly what this element exists to avoid.
       */
      onSubmit = async (event) => {
        // Cancel the native submit first, on every path — a reload is what this element
        // exists to prevent. `stopImmediatePropagation` also silences any submit handler a
        // theme script may have bound (product-form and the like), whose own native submit
        // this controller could not otherwise stop.
        event.preventDefault();
        event.stopImmediatePropagation();

        const form = event.target;

        // pz-patch56: Pflichtfelder – fehlende Felder markieren, Hinweis am Button, zum ersten Feld springen
        if (!form.checkValidity()) {
          const invalid = [...form.querySelectorAll(':invalid')].filter((el) => el.name);
          invalid.forEach((el) => el.classList.add('special-offer-form__input--invalid'));
          this.setFormError(strings().requiredMissing || 'Bitte füllen Sie die mit * markierten Pflichtfelder aus.');
          const first = invalid[0];
          if (first) {
            first.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => { first.focus({ preventScroll: true }); form.reportValidity(); }, 350);
          }
          return;
        }

        if (this.upload?.isUploading) {
          this.setFormError(strings().uploadInProgress);
          return;
        }

        if (!OfferList.read().length) return;

        this.setFormError('');
        this.submitting = true;
        this.toggleLoading(true);

        try {
          const response = await fetch(form.action, {
            method: 'POST',
            body: new FormData(form),
            headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
          });

          if (!response.ok) throw new Error(`The form responded ${response.status}`);

          // Shopify redirects to `?contact_posted=true` only on success; on a validation
          // error it re-renders the page in place, so `fetch`'s final url keeps whatever it
          // was posted to. Absence of the flag means failure — the list is kept so nothing
          // the customer entered is lost.
          const posted = new URL(response.url).searchParams.get('contact_posted') === 'true';
          if (!posted) throw new Error('Shopify did not confirm the submission');

          // Order matters: the success panel is revealed first, then the list is cleared.
          // `clear()` republishes an empty list, which `render()` would otherwise turn into
          // the empty state, painting over the thank-you message. `submitted` makes
          // `render()` stand down once the request is in.
          this.submitted = true;
          this.showSuccess({ scroll: true });
          OfferList.clear();
        } catch (error) {
          console.error('Submitting the quote request failed', error);
          this.setFormError(strings().submitFailed);
          this.submitting = false;
          this.toggleLoading(false);
        }
      };

      /**
       * The height is pinned before the fade so the page does not jump while the form
       * collapses; it is released once the success panel is in, letting the section
       * settle to its natural size.
       */
      showSuccess({ scroll }) {
        if (!this.body || !this.successState) return;

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // The thank-you message stands alone — the empty-state sections must not appear
        // once the list is cleared behind it.
        this.setPageState('submitted');

        let revealed = false;
        const reveal = () => {
          // `transitionend` and the timeout fallback can both fire; only the first counts.
          if (revealed) return;
          revealed = true;

          this.body.hidden = true;
          this.emptyState?.setAttribute('hidden', '');
          this.successState.hidden = false;

          requestAnimationFrame(() => {
            this.successState.classList.add('special-offer-form__success--visible');
            if (scroll) {
              this.successState.scrollIntoView({
                behavior: reduceMotion ? 'auto' : 'smooth',
                block: 'center',
              });
            }
          });
        };

        // No fade to wait on when motion is reduced, or when the body is already hidden —
        // the on-load `?contact_posted=true` path arrives with it hidden, and a
        // `transitionend` that never fires would leave the success panel unshown.
        if (reduceMotion || this.body.hidden) {
          reveal();
          return;
        }

        this.body.classList.add('special-offer-form__body--leaving');
        this.body.addEventListener('transitionend', reveal, { once: true });
        // Fallback: if the transition is interrupted or never runs, still reveal.
        setTimeout(reveal, 400);
      }

      toggleLoading(loading) {
        if (!this.submitButton) return;
        this.submitButton.disabled = loading;
        this.submitButton.setAttribute('aria-busy', String(loading));
        this.submitButton.classList.toggle('special-offer-form__submit--loading', loading);
      }

      setFormError(message) {
        const target = this.querySelector('[data-offer-form-error]');
        if (!target) return;

        target.textContent = message ?? '';
        target.hidden = !message;
      }

      /* ---------- helpers ---------- */

      /**
       * Drives the visibility of the empty-state sections that follow this one in the
       * template. Left unset in the theme editor, where they must stay editable.
       */
      setPageState(state) {
        if (window.Shopify?.designMode) return;
        document.documentElement.dataset.offerState = state;
      }

      setText(scope, selector, value) {
        const target = scope.querySelector(selector);
        if (!target) return;

        target.textContent = value ?? '';

        // Optional lines (variant, sku) collapse when empty so a default-variant product
        // does not leave a blank row taking vertical space.
        if (target.dataset.offerLineSku !== undefined || target.dataset.offerLineVariant !== undefined) {
          target.hidden = !value;
        }
      }

      format(template, values) {
        if (!template) return '';
        return Object.entries(values).reduce(
          (result, [key, value]) => result.replace(`[${key}]`, value),
          template
        );
      }
    }
  );
}
